import type { JSX, PointerEvent as ReactPointerEvent } from "react";
import { useRef } from "react";
import { fmtAbsTime } from "../format";

/**
 * A range-slider overview strip for panning the time window into the past (#228
 * Phase 3). It renders the full navigable span [boundsFrom, boundsTo] as a track
 * with the currently-selected [from, to] window highlighted; dragging the
 * highlighted window pans it (keeping its width), reporting the new window via
 * onChange. This is the Highcharts/Plotly "navigator" pattern: a compact overview
 * to move a zoomed window across the whole timeline without losing context.
 */
export function RangeOverview({
  from, to, boundsFrom, boundsTo, onChange
}: {
  from: number;
  to: number;
  boundsFrom: number;
  boundsTo: number;
  onChange: (from: number, to: number) => void;
}): JSX.Element {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const drag = useRef<{ startX: number; startFrom: number } | null>(null);
  const span = Math.max(1, boundsTo - boundsFrom);
  const width = to - from;
  const leftPct = ((from - boundsFrom) / span) * 100;
  const widthPct = (width / span) * 100;

  const pxToMs = (dx: number): number => {
    const el = trackRef.current;
    if (el === null) return 0;
    return (dx / el.getBoundingClientRect().width) * span;
  };

  const onDown = (e: ReactPointerEvent<HTMLDivElement>): void => {
    drag.current = { startX: e.clientX, startFrom: from };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onMove = (e: ReactPointerEvent<HTMLDivElement>): void => {
    if (drag.current === null) return;
    const deltaMs = pxToMs(e.clientX - drag.current.startX);
    // Pan keeps the window width; clamp so it never slides past the bounds.
    let nextFrom = drag.current.startFrom + deltaMs;
    nextFrom = Math.max(boundsFrom, Math.min(boundsTo - width, nextFrom));
    onChange(Math.round(nextFrom), Math.round(nextFrom + width));
  };
  const onUp = (): void => { drag.current = null; };

  return (
    <div className="cc-overview">
      <div className="cc-overview-track" ref={trackRef}>
        <div
          className="cc-overview-window"
          style={{ left: `${Math.max(0, leftPct)}%`, width: `${Math.min(100, widthPct)}%` }}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          role="slider"
          aria-label="时间窗口"
          aria-valuemin={boundsFrom}
          aria-valuemax={boundsTo}
          aria-valuenow={from}
        />
      </div>
      <div className="cc-overview-bounds">
        <span>{fmtAbsTime(boundsFrom)}</span>
        <span>{fmtAbsTime(boundsTo)}</span>
      </div>
    </div>
  );
}
